import { useMemo } from "react"
import { useDispatch } from "react-redux"
import { useQuery } from "@tanstack/react-query"
import { noop } from "lodash"
import { SelectedInstrument } from "helpers/types/instruments/selected"
import { IsoDate } from "helpers/types/dates"
import { useAccountAlias } from "resources/account"
import { createRiskSingleDateQuery, TOTAL_RISK } from "gatherers/single-date/risk"
import { Creators } from "model/apiWrapper"
import { OK, ERROR, LOADING, ResourceResult } from "helpers/data-statuses"

interface RiskData {
  values: Record<string, { id: string, label: string, value: number }>
  summary?: number
}

export const useTotalRisk = (
  instrument: SelectedInstrument | null,
  modelId: string,
  endDate: IsoDate,
  base: SelectedInstrument | null,
): ResourceResult<RiskData> => {
  const dispatch = useDispatch()
  const account = useAccountAlias()

  const query = useMemo(() => {
    if (!instrument || !modelId || !endDate || !instrument.type || !instrument.id) {
      return null
    }

    return createRiskSingleDateQuery(modelId, instrument, base, endDate, {
      metrics: [TOTAL_RISK],
      isActive: Boolean(base),
    })
  }, [instrument, modelId, endDate, base])

  const { status, data } = useQuery(
    query
      ? {
        queryKey: query.key,
        queryFn: () => new Promise((resolve, reject) => {
          dispatch(Creators.graphql(account, query.query, query.variables, resolve, reject))
        }),
        select: query.select,
        enabled: true,
      }
      : {
        queryKey: ["optimize-experiments-target-total-risk", "disabled"],
        queryFn: noop,
        enabled: false,
      },
  )

  if (status === "success") {
    return [data, OK]
  } else if (status === "error") {
    return [null, ERROR]
  } else {
    return [null, LOADING]
  }
}
